import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { NavigateOptions } from 'react-router-dom';
import { Controller, Resolver, type FieldErrors, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import { AppAdminFormLabel, AppButton, AppCard, AppSelect, AppSpace } from '@/components/atoms';
import SiteAdminNotFoundTemplate from '@/components/templates/SiteAdminNotFoundTemplate';
import { convGtJDate } from '@/utils/dateConverter';
import {
  useClinicLocationQuery,
  useCreateClinicLocationManagerMutation,
  useGetManagerWideAreaQuery,
  parseClinicLocationId,
} from '@/hooks/admin/useClinicLocationCreate';
import { useAppToast } from '@/hooks/useAppToast';
import { useStandardDeleteConfirm } from '@/hooks/useStandardDeleteConfirm';
import { isNotFoundError } from '@/utils/apiError';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import type {
  ClinicLocationManagerDetail,
  ClinicLocationManagerServiceBlock,
  ClinicLocationManagerStorePayload,
  ClinicLocationManagerUpdatePayload,
  ClinicLocationMassagerItem,
  ClinicLocationShowResponse,
} from '@/services/admin/clinic-locations';
import { getClinicLocation } from '@/services/admin/clinic-locations';
import {
  BLOCK_A2,
  BLOCK_R2,
  MANAGER_STORE_BLOCK_A2,
  MANAGER_STORE_BLOCK_R2,
  SERVICE_TYPE_A2,
  SERVICE_TYPE_BOTH,
  SERVICE_TYPE_R2,
} from '@/constants';
import {
  createClinicLocationManagerSchema,
  MASSAGER_PERIOD_END_MSG,
  MASSAGER_PERIOD_START_MSG,
  type CreateClinicLocationManagerFormData,
  type ServiceBlockFormData,
} from './create-manager-schema';
import { ServiceBlockFields } from './ServiceBlockFields';

/**
 * Edit 施術管理者: third segment 拠点:{massagers.serial_number} — serial of the massager
 * selected on initial load (DB); prefer massagers[] row, then API massager_serial_number.
 */
export function massagerSerialForManagerEditBreadcrumb(
  massagers: ClinicLocationMassagerItem[],
  massagerId: number,
  massagerSerialFromApi: number | null | undefined,
  locationId: number
): string {
  const row = massagers.find(m => (m.massager_id ?? m.id) === massagerId);
  const fromList = row?.serial_number;
  if (fromList != null && String(fromList).trim() !== '') {
    return String(fromList).trim();
  }
  if (massagerSerialFromApi != null && Number.isFinite(massagerSerialFromApi)) {
    return String(massagerSerialFromApi);
  }
  return String(locationId);
}

/** Display: {serial_number}:{name} e.g. 1: 美咲 高橋 */
function massagerToOption(m: ClinicLocationMassagerItem, fallbackIndex: number) {
  const id = m.massager_id ?? m.id;
  const sn =
    m.serial_number != null && String(m.serial_number).trim() !== ''
      ? String(m.serial_number).trim()
      : String(fallbackIndex);
  const label = `${sn}: ${m.name ?? ''}`.trimEnd();
  return { value: id, label };
}

const emptyWideAreaRow = {
  wide_area_union_code: undefined as string | undefined,
  wide_area_union_number: undefined as string | undefined,
  prefecture_number: undefined as string | undefined,
};

/** Edit/create guard: 拠点のサービス区分 changed while form still has data for removed side. */
const CLINIC_LOCATION_MANAGER_SERVICE_TYPE_MISMATCH_TOAST =
  '拠点のサービス区分が変更されました。施術管理者を再度登録してください。';

const defaultServiceBlock: ServiceBlockFormData = {
  is_none_disp: false,
  available_from: '',
  available_to: null,
  location_manager_number: '',
  wide_area_union_rows: [emptyWideAreaRow],
  am_time_start: null,
  am_time_end: null,
  pm_time_start: null,
  pm_time_end: null,
  is_am_monday: false,
  is_am_tuesday: false,
  is_am_wednesday: false,
  is_am_thursday: false,
  is_am_friday: false,
  is_am_saturday: false,
  is_am_sunday: false,
  is_pm_monday: false,
  is_pm_tuesday: false,
  is_pm_wednesday: false,
  is_pm_thursday: false,
  is_pm_friday: false,
  is_pm_saturday: false,
  is_pm_sunday: false,
};

function hasBlockApiShape(d: ClinicLocationManagerDetail): boolean {
  return (
    Object.prototype.hasOwnProperty.call(d, MANAGER_STORE_BLOCK_A2) ||
    Object.prototype.hasOwnProperty.call(d, MANAGER_STORE_BLOCK_R2)
  );
}

type ManagerApiErrors = Record<string, string[] | string> | undefined;
const DUPLICATE_PERIOD_MESSAGE = '適用期間が重複しています。';

function hasDuplicatePeriodError(
  data: { message?: string; errors?: ManagerApiErrors } | undefined
): boolean {
  if (!data) return false;
  if (data.message === DUPLICATE_PERIOD_MESSAGE) return true;
  const errors = data.errors;
  if (!errors) return false;
  return Object.values(errors).some(val => {
    if (typeof val === 'string') return val === DUPLICATE_PERIOD_MESSAGE;
    return Array.isArray(val) && val.includes(DUPLICATE_PERIOD_MESSAGE);
  });
}

function firstApiErrorMessage(val: string[] | string | undefined): string | undefined {
  if (val == null) return undefined;
  if (typeof val === 'string') return val;
  const first = val[0];
  return typeof first === 'string' ? first : undefined;
}

function toastForManagerFormValidationErrors(
  errors: FieldErrors<CreateClinicLocationManagerFormData>,
  toastError: (msg: string) => void
): void {
  /** はり・きゅう (a2) → マッサージ (r2) の順で、施術者適用期間エラーを集約（両方とも表示） */
  const blocks: Array<keyof Pick<CreateClinicLocationManagerFormData, 'a2' | 'r2'>> = ['a2', 'r2'];
  const orderedMsgs: string[] = [];
  const seen = new Set<string>();
  for (const block of blocks) {
    const b = errors[block] as FieldErrors<ServiceBlockFormData> | undefined;
    if (!b) continue;
    for (const field of ['available_from', 'available_to'] as const) {
      const fe = b[field];
      const msg = fe && typeof fe === 'object' && 'message' in fe ? fe.message : undefined;
      if (
        typeof msg === 'string' &&
        (msg === MASSAGER_PERIOD_START_MSG || msg === MASSAGER_PERIOD_END_MSG) &&
        !seen.has(msg)
      ) {
        seen.add(msg);
        orderedMsgs.push(msg);
      }
    }
  }
  if (orderedMsgs.length > 0) {
    toastError(orderedMsgs.join('\n'));
    return;
  }
  toastError('エラーが発生しました。');
}

/**
 * Map Laravel manager validation keys to react-hook-form paths (a2/r2 blocks, massager_id).
 * legacyBlockPrefix: flat `available_*` from legacy single-block update API.
 */
function applyManagerApiValidationErrors(
  form: UseFormReturn<CreateClinicLocationManagerFormData>,
  apiErrors: ManagerApiErrors,
  options: {
    legacyBlockPrefix?: typeof BLOCK_A2 | typeof BLOCK_R2;
    applyLegacyToBothBlocks?: boolean;
    mirrorDuplicatePeriodToBothBlocks?: boolean;
    /** false = disabled block: do not set a2.* field errors (no red border on read-only UI). */
    a2Enabled?: boolean;
    r2Enabled?: boolean;
  } = {}
): boolean {
  if (!apiErrors) return false;
  let applied = false;
  const allowA2 = options.a2Enabled !== false;
  const allowR2 = options.r2Enabled !== false;

  const setBlockPeriodError = (
    block: typeof BLOCK_A2 | typeof BLOCK_R2,
    suffix: 'available_from' | 'available_to',
    msg: string
  ): void => {
    if (block === BLOCK_A2 && !allowA2) return;
    if (block === BLOCK_R2 && !allowR2) return;
    form.setError(`${block}.${suffix}`, { type: 'server', message: msg });
    applied = true;
  };

  const massagerMsg = firstApiErrorMessage(apiErrors.massager_id);
  if (massagerMsg) {
    form.setError('massager_id', { type: 'server', message: massagerMsg });
    applied = true;
  }

  for (const suffix of ['available_from', 'available_to'] as const) {
    for (const [apiPrefix, formPrefix] of [
      [MANAGER_STORE_BLOCK_A2, BLOCK_A2],
      [MANAGER_STORE_BLOCK_R2, BLOCK_R2],
    ] as const) {
      const key = `${apiPrefix}.${suffix}`;
      const msg = firstApiErrorMessage(apiErrors[key]);
      if (msg) {
        setBlockPeriodError(formPrefix, suffix, msg);
      }
    }
  }

  const legacy = options.legacyBlockPrefix;
  const applyLegacyToBothBlocks = options.applyLegacyToBothBlocks === true;
  const mirrorDuplicatePeriodToBothBlocks = options.mirrorDuplicatePeriodToBothBlocks === true;
  if (legacy) {
    for (const field of ['available_from', 'available_to'] as const) {
      const msg = firstApiErrorMessage(apiErrors[field]);
      if (msg) {
        if (mirrorDuplicatePeriodToBothBlocks && msg === DUPLICATE_PERIOD_MESSAGE) {
          setBlockPeriodError(BLOCK_A2, field, msg);
          setBlockPeriodError(BLOCK_R2, field, msg);
          continue;
        }
        setBlockPeriodError(legacy, field, msg);
      }
    }
  } else if (applyLegacyToBothBlocks) {
    for (const field of ['available_from', 'available_to'] as const) {
      const msg = firstApiErrorMessage(apiErrors[field]);
      if (msg) {
        setBlockPeriodError(BLOCK_A2, field, msg);
        setBlockPeriodError(BLOCK_R2, field, msg);
      }
    }
  }

  if (mirrorDuplicatePeriodToBothBlocks) {
    for (const suffix of ['available_from', 'available_to'] as const) {
      for (const apiPrefix of [MANAGER_STORE_BLOCK_A2, MANAGER_STORE_BLOCK_R2] as const) {
        const msg = firstApiErrorMessage(apiErrors[`${apiPrefix}.${suffix}`]);
        if (msg === DUPLICATE_PERIOD_MESSAGE) {
          setBlockPeriodError(BLOCK_A2, suffix, msg);
          setBlockPeriodError(BLOCK_R2, suffix, msg);
        }
      }
    }
  }

  return applied;
}

/** True when no 保険者別番号 cell has data (used to merge massager wide-area prefill on edit). */
function wideAreaRowsHaveNoInsurerData(
  rows: ServiceBlockFormData['wide_area_union_rows'] | undefined
): boolean {
  if (!rows?.length) return true;
  return rows.every(row => {
    const c = (row?.wide_area_union_code ?? '').toString().trim();
    const n = (row?.wide_area_union_number ?? '').toString().trim();
    const p = (row?.prefecture_number ?? '').toString().trim();
    return c === '' && n === '' && p === '';
  });
}

/**
 * When 施術者 is selected (id > 0), enable block only if list row exists and has cert.
 * Do not use (!massager || has_cert): missing row was treated as "no restriction" and API prefill ran after clear.
 */
function isA2BlockEnabledForMassager(
  showA2Block: boolean,
  massagerIdNum: number | null,
  massager: ClinicLocationMassagerItem | null | undefined
): boolean {
  if (!showA2Block) return false;
  if (massagerIdNum == null || massagerIdNum <= 0) return true;
  return !!massager?.has_a2_cert;
}

function isR2BlockEnabledForMassager(
  showR2Block: boolean,
  massagerIdNum: number | null,
  massager: ClinicLocationMassagerItem | null | undefined
): boolean {
  if (!showR2Block) return false;
  if (massagerIdNum == null || massagerIdNum <= 0) return true;
  return !!massager?.has_r2_cert;
}

function mapServiceBlockToFormBox(
  block: ClinicLocationManagerServiceBlock | null | undefined
): ServiceBlockFormData {
  if (!block) {
    return { ...defaultServiceBlock };
  }
  const rows = block.wide_area_union_rows?.length
    ? block.wide_area_union_rows.map(r => ({
        wide_area_union_code: r.wide_area_union_code ?? undefined,
        wide_area_union_number: r.wide_area_union_number ?? undefined,
        prefecture_number: r.prefecture_number ?? undefined,
      }))
    : [emptyWideAreaRow];
  return {
    is_none_disp: block.is_none_disp ?? false,
    available_from: block.available_from ?? '',
    available_to: block.available_to === '2100-12-31' ? null : (block.available_to ?? null),
    location_manager_number: block.location_manager_number ?? '',
    wide_area_union_rows: rows,
    am_time_start: block.am_time_start ?? null,
    am_time_end: block.am_time_end ?? null,
    pm_time_start: block.pm_time_start ?? null,
    pm_time_end: block.pm_time_end ?? null,
    is_am_monday: block.is_am_monday ?? false,
    is_am_tuesday: block.is_am_tuesday ?? false,
    is_am_wednesday: block.is_am_wednesday ?? false,
    is_am_thursday: block.is_am_thursday ?? false,
    is_am_friday: block.is_am_friday ?? false,
    is_am_saturday: block.is_am_saturday ?? false,
    is_am_sunday: block.is_am_sunday ?? false,
    is_pm_monday: block.is_pm_monday ?? false,
    is_pm_tuesday: block.is_pm_tuesday ?? false,
    is_pm_wednesday: block.is_pm_wednesday ?? false,
    is_pm_thursday: block.is_pm_thursday ?? false,
    is_pm_friday: block.is_pm_friday ?? false,
    is_pm_saturday: block.is_pm_saturday ?? false,
    is_pm_sunday: block.is_pm_sunday ?? false,
  };
}

/**
 * Edit init: only map API block data for services the 拠点 still offers; other side stays empty + disabled via show*Block.
 */
function mapManagerDetailToFormData(
  d: ClinicLocationManagerDetail,
  locationService: string | null
): CreateClinicLocationManagerFormData {
  const locSt = (locationService ?? '').toUpperCase();
  const locOffersA2 = locSt === SERVICE_TYPE_A2 || locSt === SERVICE_TYPE_BOTH;
  const locOffersR2 = locSt === SERVICE_TYPE_R2 || locSt === SERVICE_TYPE_BOTH;

  if (hasBlockApiShape(d)) {
    return {
      massager_id: d.massager_id,
      a2: locOffersA2 ? mapServiceBlockToFormBox(d.BLOCK_A2) : { ...defaultServiceBlock },
      r2: locOffersR2 ? mapServiceBlockToFormBox(d.BLOCK_R2) : { ...defaultServiceBlock },
    };
  }

  const rows = d.wide_area_union_rows?.length
    ? d.wide_area_union_rows.map(r => ({
        wide_area_union_code: r.wide_area_union_code ?? undefined,
        wide_area_union_number: r.wide_area_union_number ?? undefined,
        prefecture_number: r.prefecture_number ?? undefined,
      }))
    : [emptyWideAreaRow];
  const box: ServiceBlockFormData = {
    is_none_disp: d.is_none_disp,
    available_from: d.available_from,
    available_to: d.available_to === '2100-12-31' ? null : (d.available_to ?? null),
    location_manager_number: d.location_manager_number ?? '',
    wide_area_union_rows: rows,
    am_time_start: d.am_time_start ?? null,
    am_time_end: d.am_time_end ?? null,
    pm_time_start: d.pm_time_start ?? null,
    pm_time_end: d.pm_time_end ?? null,
    is_am_monday: d.is_am_monday,
    is_am_tuesday: d.is_am_tuesday,
    is_am_wednesday: d.is_am_wednesday,
    is_am_thursday: d.is_am_thursday,
    is_am_friday: d.is_am_friday,
    is_am_saturday: d.is_am_saturday,
    is_am_sunday: d.is_am_sunday,
    is_pm_monday: d.is_pm_monday,
    is_pm_tuesday: d.is_pm_tuesday,
    is_pm_wednesday: d.is_pm_wednesday,
    is_pm_thursday: d.is_pm_thursday,
    is_pm_friday: d.is_pm_friday,
    is_pm_saturday: d.is_pm_saturday,
    is_pm_sunday: d.is_pm_sunday,
  };
  const st = (d.service_type ?? '').toUpperCase();
  const isR2Primary = st === SERVICE_TYPE_R2;
  const emptySide = { ...defaultServiceBlock };
  const a2Form = !locOffersA2 ? emptySide : isR2Primary ? emptySide : box;
  const r2Form = !locOffersR2 ? emptySide : isR2Primary ? box : emptySide;
  return {
    massager_id: d.massager_id,
    a2: a2Form,
    r2: r2Form,
  };
}

type FormPageProps = {
  locationId: number;
  clinicId: number;
  /** Text after「拠点:」in breadcrumb (create: location id; edit: massager serial on load). */
  kyotenBreadcrumbSuffix: string;
  locationName: string;
  locationNameKana: string | undefined;
  availableFrom: string;
  availableTo: string | null;
  massagers: ClinicLocationMassagerItem[];
  /** Clinic location service (from clinic_locations.service / API). Values: SERVICE_TYPE_*. */
  locationService: string | null;
  siteType: string;
  navigate: (path: string, options?: NavigateOptions) => void;
  editMode?: boolean;
  initialData?: ClinicLocationManagerDetail | null;
  /** Edit: disable back while update mutation is in flight (create uses internal mutation state). */
  isUpdatePending?: boolean;
  onUpdate?: (payload: ClinicLocationManagerUpdatePayload) => Promise<void>;
  /** Edit: 更新前に拠点 show を再取得し、施術者一覧の最新で検証する */
  refetchClinicLocation?: () => Promise<{ data?: ClinicLocationShowResponse | undefined }>;
  /** Edit: service 不一致時に manager detail を再取得して form を同期 */
  refetchManager?: () => Promise<unknown>;
  /** Edit: delete after confirm (拠点詳細へ遷移は呼び出し側). */
  onDelete?: () => Promise<void>;
  isDeletePending?: boolean;
  auditInfo?: React.ReactNode;
};

function boxToPayload(
  box: ServiceBlockFormData | undefined
): ClinicLocationManagerServiceBlock | null {
  if (!box || !box.available_from?.trim()) return null;
  return {
    available_from: box.available_from,
    available_to: box.available_to ?? null,
    location_manager_number: box.location_manager_number?.trim() || null,
    is_none_disp: box.is_none_disp ?? false,
    wide_area_union_rows: (box.wide_area_union_rows ?? []).map(r => ({
      wide_area_union_code: r.wide_area_union_code ?? null,
      wide_area_union_number: r.wide_area_union_number ?? null,
      prefecture_number: r.prefecture_number ?? null,
    })),
    am_time_start: box.am_time_start?.trim() || null,
    am_time_end: box.am_time_end?.trim() || null,
    pm_time_start: box.pm_time_start?.trim() || null,
    pm_time_end: box.pm_time_end?.trim() || null,
    is_am_monday: box.is_am_monday ?? false,
    is_am_tuesday: box.is_am_tuesday ?? false,
    is_am_wednesday: box.is_am_wednesday ?? false,
    is_am_thursday: box.is_am_thursday ?? false,
    is_am_friday: box.is_am_friday ?? false,
    is_am_saturday: box.is_am_saturday ?? false,
    is_am_sunday: box.is_am_sunday ?? false,
    is_pm_monday: box.is_pm_monday ?? false,
    is_pm_tuesday: box.is_pm_tuesday ?? false,
    is_pm_wednesday: box.is_pm_wednesday ?? false,
    is_pm_thursday: box.is_pm_thursday ?? false,
    is_pm_friday: box.is_pm_friday ?? false,
    is_pm_saturday: box.is_pm_saturday ?? false,
    is_pm_sunday: box.is_pm_sunday ?? false,
  };
}

export function CreateClinicLocationManagerForm({
  locationId,
  clinicId,
  kyotenBreadcrumbSuffix,
  locationName,
  locationNameKana,
  availableFrom,
  availableTo,
  massagers,
  locationService,
  siteType,
  navigate,
  editMode = false,
  initialData = null,
  isUpdatePending = false,
  onUpdate,
  refetchClinicLocation,
  refetchManager,
  onDelete,
  isDeletePending = false,
  auditInfo,
}: FormPageProps) {
  const createDefaultValues = useMemo<CreateClinicLocationManagerFormData>(
    () => ({
      massager_id: 0,
      a2: { ...defaultServiceBlock },
      r2: { ...defaultServiceBlock },
    }),
    []
  );

  const defaultValues = useMemo<CreateClinicLocationManagerFormData>(
    () =>
      initialData ? mapManagerDetailToFormData(initialData, locationService) : createDefaultValues,
    [initialData, locationService, createDefaultValues]
  );

  const locSt = (locationService ?? '').toUpperCase();
  const showA2Block = locSt === SERVICE_TYPE_A2 || locSt === SERVICE_TYPE_BOTH;
  const showR2Block = locSt === SERVICE_TYPE_R2 || locSt === SERVICE_TYPE_BOTH;
  /** 拠点が BOTH のとき、資格のある各ブロックで適用期間に必須表示 */
  const locationServiceBoth = locSt === SERVICE_TYPE_BOTH;

  const resolver: Resolver<CreateClinicLocationManagerFormData> = useCallback(
    (values, context, options) => {
      const massagerId = values.massager_id;
      const selectedMassager =
        massagerId != null && massagerId > 0
          ? (massagers.find(m => (m.massager_id ?? m.id) === massagerId) ?? null)
          : null;
      const mid = massagerId != null && massagerId > 0 ? massagerId : null;
      const enableA2 = isA2BlockEnabledForMassager(showA2Block, mid, selectedMassager);
      const enableR2 = isR2BlockEnabledForMassager(showR2Block, mid, selectedMassager);
      const schema = createClinicLocationManagerSchema(
        enableA2,
        enableR2,
        selectedMassager
          ? {
              a2: {
                available_from: selectedMassager.available_from,
                available_to: selectedMassager.available_to,
              },
              r2: {
                available_from: selectedMassager.available_from_r2 ?? null,
                available_to: selectedMassager.available_to_r2 ?? null,
              },
            }
          : null
      );
      return zodResolver(schema)(values, context, options);
    },
    [massagers, showA2Block, showR2Block]
  );

  const form = useTrimmedForm<CreateClinicLocationManagerFormData>({
    resolver,
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    const subscription = form.watch((_value, { name, type }) => {
      if (type !== 'change' || !name) return;
      form.clearErrors(name as keyof CreateClinicLocationManagerFormData);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  /** Tracks last massager id for cert-based block reset; edit skips first sync with API massager_id. */
  const previousMassagerIdForCertBlocksRef = useRef<number | null>(null);

  /** Bumped after parent setValue on wide_area_union_rows so useFieldArray.replace matches form (same length). */
  const [wideAreaFieldArraySync, setWideAreaFieldArraySync] = useState(0);
  const bumpWideAreaFieldArraySync = useCallback(() => {
    setWideAreaFieldArraySync(n => n + 1);
  }, []);

  useEffect(() => {
    if (editMode && initialData) {
      previousMassagerIdForCertBlocksRef.current = null;
      form.reset(mapManagerDetailToFormData(initialData, locationService));
    }
  }, [editMode, initialData, locationService, form]);

  const massagerIdNum = form.watch('massager_id') > 0 ? form.watch('massager_id') : null;
  const lastPrefilledMassagerIdRef = useRef<number | null>(null);

  const wideAreaQuery = useGetManagerWideAreaQuery(siteType, locationId, massagerIdNum);

  /** 施術者変更時: reset wide_area, clear validation errors for both blocks. */
  useEffect(() => {
    if (editMode) return;
    if (massagerIdNum != null) {
      if (lastPrefilledMassagerIdRef.current !== massagerIdNum) {
        const oneRow = [{ ...emptyWideAreaRow }];
        form.setValue(`${BLOCK_A2}.wide_area_union_rows`, oneRow);
        form.setValue(`${BLOCK_R2}.wide_area_union_rows`, oneRow);
        form.clearErrors([BLOCK_A2, BLOCK_R2]);
        bumpWideAreaFieldArraySync();
        lastPrefilledMassagerIdRef.current = null;
      }
    } else {
      lastPrefilledMassagerIdRef.current = null;
    }
  }, [editMode, massagerIdNum, form, bumpWideAreaFieldArraySync]);

  /**
   * When massager changes: clear disabled service blocks (資格なし / 拠点サービス外).
   * Edit: skip initial load (same massager_id as API) so both blocks stay filled; after user changes 施術者, clear disabled blocks.
   * useLayoutEffect + bumpWideAreaFieldArraySync: useFieldArray must replace() when length unchanged after setValue.
   */
  useLayoutEffect(() => {
    const massager =
      massagerIdNum != null && massagerIdNum > 0
        ? massagers.find(m => (m.massager_id ?? m.id) === massagerIdNum)
        : undefined;

    if (!massagerIdNum) {
      previousMassagerIdForCertBlocksRef.current = null;
    } else {
      const prevMassagerId = previousMassagerIdForCertBlocksRef.current;

      if (editMode && initialData != null) {
        const initialMassagerId = initialData.massager_id;
        if (
          prevMassagerId === null &&
          initialMassagerId != null &&
          massagerIdNum === initialMassagerId
        ) {
          previousMassagerIdForCertBlocksRef.current = massagerIdNum;
          return;
        }
      }

      previousMassagerIdForCertBlocksRef.current = massagerIdNum;
    }

    const a2Enabled = isA2BlockEnabledForMassager(showA2Block, massagerIdNum, massager);
    const r2Enabled = isR2BlockEnabledForMassager(showR2Block, massagerIdNum, massager);
    if (!a2Enabled || !r2Enabled) {
      const clearedBlock = (): ServiceBlockFormData => ({
        ...defaultServiceBlock,
        wide_area_union_rows: [{ ...emptyWideAreaRow }],
      });
      const next: CreateClinicLocationManagerFormData = { ...form.getValues() };
      if (!a2Enabled) next.a2 = clearedBlock();
      if (!r2Enabled) next.r2 = clearedBlock();
      form.reset(next);
      if (!a2Enabled) form.clearErrors(BLOCK_A2);
      if (!r2Enabled) form.clearErrors(BLOCK_R2);
      bumpWideAreaFieldArraySync();
    }
  }, [
    massagerIdNum,
    massagers,
    showA2Block,
    showR2Block,
    form,
    editMode,
    initialData,
    bumpWideAreaFieldArraySync,
  ]);

  /** API 取得後: prefill wide_area per block (A2/R2) by service_type. Create: always. Edit: only blocks with no 保険者別番号 data yet. */
  useEffect(() => {
    if (massagerIdNum == null) return;
    if (editMode && initialData == null) return;
    const massager = massagers.find(m => (m.massager_id ?? m.id) === massagerIdNum);
    const a2Enabled = isA2BlockEnabledForMassager(showA2Block, massagerIdNum, massager);
    const r2Enabled = isR2BlockEnabledForMassager(showR2Block, massagerIdNum, massager);
    /** Do not touch form when both blocks are disabled (avoids refilling API rows after reset). */
    if (!a2Enabled && !r2Enabled) return;
    if (!wideAreaQuery.data?.data) return;
    /** Avoid applying previous massager's cached rows while the new massager query is placeholder. */
    if (wideAreaQuery.isPlaceholderData) return;
    const rows = wideAreaQuery.data.data;
    if (!Array.isArray(rows)) return;
    const a2NeedsPrefill =
      !editMode ||
      wideAreaRowsHaveNoInsurerData(form.getValues(`${BLOCK_A2}.wide_area_union_rows`));
    const r2NeedsPrefill =
      !editMode ||
      wideAreaRowsHaveNoInsurerData(form.getValues(`${BLOCK_R2}.wide_area_union_rows`));
    if (!a2NeedsPrefill && !r2NeedsPrefill) return;
    const toRow = (r: (typeof rows)[number]) => ({
      wide_area_union_code: r.wide_area_union_code ?? undefined,
      wide_area_union_number: r.wide_area_union_number ?? undefined,
      prefecture_number: r.prefecture_number ?? undefined,
    });
    const st = (s: string | null | undefined) => (s ?? '').toUpperCase();
    const a2Rows = rows.filter(r => {
      const stVal = st(r.service_type);
      return stVal === SERVICE_TYPE_A2 || stVal === SERVICE_TYPE_BOTH || stVal === '';
    });
    const r2Rows = rows.filter(r => {
      const stVal = st(r.service_type);
      return stVal === SERVICE_TYPE_R2 || stVal === SERVICE_TYPE_BOTH || stVal === '';
    });
    const buildRows = (mapped: typeof a2Rows) => {
      const arr = mapped.map(toRow).slice(0, 30);
      // One template empty row at top only when under max (else 30 data rows + empty would exceed .max(30)).
      if (arr.length < 30) {
        return [emptyWideAreaRow, ...arr];
      }
      return arr;
    };
    let prefilled = false;
    if (a2Enabled && a2NeedsPrefill) {
      form.setValue(`${BLOCK_A2}.wide_area_union_rows`, buildRows(a2Rows));
      prefilled = true;
    }
    if (r2Enabled && r2NeedsPrefill) {
      form.setValue(`${BLOCK_R2}.wide_area_union_rows`, buildRows(r2Rows));
      prefilled = true;
    }
    if (prefilled) bumpWideAreaFieldArraySync();
    if (!editMode) lastPrefilledMassagerIdRef.current = massagerIdNum;
  }, [
    editMode,
    initialData,
    massagerIdNum,
    massagers,
    showA2Block,
    showR2Block,
    wideAreaQuery.data,
    wideAreaQuery.isPlaceholderData,
    form,
    bumpWideAreaFieldArraySync,
  ]);

  const toast = useAppToast();
  const openStandardDeleteConfirm = useStandardDeleteConfirm();
  const createMutation = useCreateClinicLocationManagerMutation();

  const breadcrumbItems = useMemo(
    () => [
      { title: <Link to={`/${siteType}/admin/home`}>Home</Link> },
      { title: <Link to={`/${siteType}/admin/clinic_locations`}>拠点一覧</Link> },
      {
        title: (
          <Link to={`/${siteType}/admin/clinic_locations/${locationId}`}>
            拠点:{kyotenBreadcrumbSuffix}
          </Link>
        ),
      },
      {
        title: editMode ? '施術管理者編集' : '施術管理者作成',
      },
    ],
    [siteType, locationId, kyotenBreadcrumbSuffix, editMode]
  );

  const pageTitle = editMode ? '施術管理者編集' : '施術管理者作成';

  /** 拠点 location table: 和暦; default 2100-12-31 or null/empty → end date hidden (FROM -). */
  const locationPeriodDisplay = useMemo(() => {
    const fromStr = availableFrom ? convGtJDate(dayjs(availableFrom)) : '';
    const toRaw = availableTo?.trim() ?? '';
    const isIndefinite = !toRaw || toRaw === '2100-12-31';
    if (isIndefinite) {
      return `${fromStr} -`;
    }
    return `${fromStr} - ${convGtJDate(dayjs(availableTo))}`;
  }, [availableFrom, availableTo]);

  /** Spec: 拠点名カナ empty or not katakana → show （） */
  const locationKanaDisplay = useMemo(() => {
    const kana = (locationNameKana ?? '').trim();
    if (!kana) return '（）';
    const katakanaRegex = /^[ァ-ヶｦ-ﾟー 　・]+$/u;
    if (!katakanaRegex.test(kana)) return '（）';
    return `（${kana}）`;
  }, [locationNameKana]);

  const handleSubmit = form.handleSubmit(
    async values => {
      let massagersForCheck = massagers;
      let locationRefetchBody: ClinicLocationShowResponse | undefined;

      if (editMode && onUpdate && refetchClinicLocation) {
        try {
          const refetchResult = await refetchClinicLocation();
          locationRefetchBody = refetchResult.data;
          const freshMassagers = refetchResult.data?.data?.massagers;
          if (freshMassagers != null) {
            massagersForCheck = freshMassagers;
          }
        } catch {
          toast.error('エラーが発生しました。');
          return;
        }
      }

      const validMassagerIds = massagersForCheck
        .map(m => m.massager_id ?? m.id)
        .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id) && id > 0);

      if (values.massager_id > 0 && !validMassagerIds.includes(values.massager_id)) {
        form.setError('massager_id', {
          type: 'manual',
          message: '選択された施術者は正しくありません。',
        });
        toast.error('エラーが発生しました。');
        return;
      }

      if (editMode && onUpdate) {
        const freshServiceRaw = locationRefetchBody?.data?.service ?? locationService;
        const freshLocSt = (freshServiceRaw ?? '').toUpperCase();
        const freshShowA2 = freshLocSt === SERVICE_TYPE_A2 || freshLocSt === SERVICE_TYPE_BOTH;
        const freshShowR2 = freshLocSt === SERVICE_TYPE_R2 || freshLocSt === SERVICE_TYPE_BOTH;
        const pA2Check = boxToPayload(values.a2);
        const pR2Check = boxToPayload(values.r2);
        if ((pA2Check !== null && !freshShowA2) || (pR2Check !== null && !freshShowR2)) {
          toast.error('エラーが発生しました。');
          await refetchManager?.();
          return;
        }

        const selectedMassagerForEdit =
          values.massager_id != null && values.massager_id > 0
            ? (massagersForCheck.find(m => (m.massager_id ?? m.id) === values.massager_id) ?? null)
            : null;
        const midEdit =
          values.massager_id != null && values.massager_id > 0 ? values.massager_id : null;
        const enableA2Edit = isA2BlockEnabledForMassager(
          showA2Block,
          midEdit,
          selectedMassagerForEdit
        );
        const enableR2Edit = isR2BlockEnabledForMassager(
          showR2Block,
          midEdit,
          selectedMassagerForEdit
        );

        if (initialData && hasBlockApiShape(initialData)) {
          const payload: ClinicLocationManagerUpdatePayload = { massager_id: values.massager_id };
          if (enableA2Edit) {
            const p = boxToPayload(values.a2);
            if (p) payload.BLOCK_A2 = p;
          } else {
            payload.BLOCK_A2 = null;
          }
          if (enableR2Edit) {
            const p = boxToPayload(values.r2);
            if (p) payload.BLOCK_R2 = p;
          } else {
            payload.BLOCK_R2 = null;
          }
          try {
            await onUpdate(payload);
            toast.success('施術管理者を更新しました。');
          } catch (err) {
            if (isNotFoundError(err)) {
              navigate(`/${siteType}/admin/404`, { replace: true });
              return;
            }
            const data = (err as { response?: { data?: { errors?: ManagerApiErrors } } })?.response
              ?.data;
            const applied = applyManagerApiValidationErrors(form, data?.errors, {
              applyLegacyToBothBlocks: true,
              mirrorDuplicatePeriodToBothBlocks: true,
              a2Enabled: enableA2Edit,
              r2Enabled: enableR2Edit,
            });
            if (applied) toast.error('エラーが発生しました。');
            else if (hasDuplicatePeriodError(data))
              toast.error('エラーが発生しました。');
            else toast.error('エラーが発生しました。');
          }
          return;
        }

        const st = (initialData?.service_type ?? '').toUpperCase();
        const block = st === SERVICE_TYPE_R2 ? values.r2 : values.a2;
        const payload = boxToPayload(block);
        if (!payload) return;
        const legacyPrefix = st === SERVICE_TYPE_R2 ? BLOCK_R2 : BLOCK_A2;
        try {
          await onUpdate({ massager_id: values.massager_id, ...payload });
          toast.success('施術管理者を更新しました。');
        } catch (err) {
          if (isNotFoundError(err)) {
            navigate(`/${siteType}/admin/404`, { replace: true });
            return;
          }
          const data = (err as { response?: { data?: { errors?: ManagerApiErrors } } })?.response
            ?.data;
          const applied = applyManagerApiValidationErrors(form, data?.errors, {
            legacyBlockPrefix: legacyPrefix,
            mirrorDuplicatePeriodToBothBlocks: true,
            a2Enabled: enableA2Edit,
            r2Enabled: enableR2Edit,
          });
          if (applied) toast.error('エラーが発生しました。');
          else if (hasDuplicatePeriodError(data))
            toast.error('エラーが発生しました。');
          else toast.error('エラーが発生しました。');
        }
        return;
      }
      if (clinicId <= 0) return;

      const selectedMassager =
        values.massager_id != null && values.massager_id > 0
          ? (massagers.find(m => (m.massager_id ?? m.id) === values.massager_id) ?? null)
          : null;
      const mid = values.massager_id != null && values.massager_id > 0 ? values.massager_id : null;
      const enableA2 = isA2BlockEnabledForMassager(showA2Block, mid, selectedMassager);
      const enableR2 = isR2BlockEnabledForMassager(showR2Block, mid, selectedMassager);

      const a2Payload = enableA2 ? boxToPayload(values.a2) : null;
      const r2Payload = enableR2 ? boxToPayload(values.r2) : null;
      if (!a2Payload && !r2Payload) {
        form.setError('massager_id', {
          type: 'manual',
          message: 'A2またはR2のいずれかに有効期限FROMを入力してください。',
        });
        toast.error('エラーが発生しました。');
        return;
      }

      if (selectedMassager) {
        if (a2Payload && !selectedMassager.has_a2_cert) {
          form.setError('massager_id', {
            type: 'manual',
            message:
              '選択した施術師はあん摩マッサージ指圧師の資格の登録が無いため、はり・きゅうの施術管理者として登録出来ません。',
          });
          toast.error('エラーが発生しました。');
          return;
        }
        if (r2Payload && !selectedMassager.has_r2_cert) {
          form.setError('massager_id', {
            type: 'manual',
            message:
              '選択した施術師ははり・きゅう師の資格の登録が無いため、マッサージの施術管理者として登録出来ません。',
          });
          toast.error('エラーが発生しました。');
          return;
        }
      }
      const fullPayload: ClinicLocationManagerStorePayload = {
        clinic_id: clinicId,
        massager_id: values.massager_id,
        ...(a2Payload ? { [MANAGER_STORE_BLOCK_A2]: a2Payload } : {}),
        ...(r2Payload ? { [MANAGER_STORE_BLOCK_R2]: r2Payload } : {}),
      };

      let freshLocation;
      try {
        freshLocation = await getClinicLocation(siteType, locationId);
      } catch {
        toast.error('エラーが発生しました。');
        return;
      }
      const freshService = freshLocation?.data?.service ?? null;
      const initialServiceNorm = (locationService ?? '').toLowerCase();
      const freshServiceNorm = (freshService ?? '').toLowerCase();
      if (initialServiceNorm !== freshServiceNorm) {
        sessionStorage.setItem(
          'clinic_location_manager_service_changed',
          CLINIC_LOCATION_MANAGER_SERVICE_TYPE_MISMATCH_TOAST
        );
        window.location.reload();
        return;
      }

      try {
        await createMutation.mutateAsync({
          siteType,
          clinicLocationId: locationId,
          payload: fullPayload,
        });
      } catch (err) {
        if (isNotFoundError(err)) {
          navigate(`/${siteType}/admin/404`, { replace: true });
          return;
        }
        const data = (err as { response?: { data?: { errors?: ManagerApiErrors } } })?.response
          ?.data;
        const applied = applyManagerApiValidationErrors(form, data?.errors, {
          applyLegacyToBothBlocks: true,
          mirrorDuplicatePeriodToBothBlocks: true,
          a2Enabled: enableA2,
          r2Enabled: enableR2,
        });
        if (applied) toast.error('エラーが発生しました。');
        else if (hasDuplicatePeriodError(data))
          toast.error('エラーが発生しました。');
        else toast.error('エラーが発生しました。');
        return;
      }
      toast.success('施術管理者を登録しました。');
      navigate(`/${siteType}/admin/clinic_locations/${locationId}`);
    },
    errors => toastForManagerFormValidationErrors(errors, m => toast.error(m))
  );

  const handleBack = () => {
    navigate(`/${siteType}/admin/clinic_locations/${locationId}`);
  };

  const selectedMassager = useMemo(() => {
    const id = form.watch('massager_id');
    if (!id || id <= 0) return null;
    return massagers.find(m => (m.massager_id ?? m.id) === id) ?? null;
  }, [massagers, form.watch('massager_id')]);

  const enableA2Block = isA2BlockEnabledForMassager(showA2Block, massagerIdNum, selectedMassager);
  const enableR2Block = isR2BlockEnabledForMassager(showR2Block, massagerIdNum, selectedMassager);

  const massagerOptions = useMemo(() => {
    const sortKeyFrom = (m: ClinicLocationMassagerItem) =>
      m.available_from?.trim() || m.available_from_r2?.trim() || '';
    const sorted = [...massagers].sort((a, b) =>
      sortKeyFrom(b).localeCompare(sortKeyFrom(a))
    );
    return sorted.map((m, i) => massagerToOption(m, i + 1)).filter(o => o.value);
  }, [massagers]);

  return (
    <div className="p-6">
      <AppSpace orientation="vertical" className="w-full" size="large">
        <nav className="rounded bg-gray-100 px-4 py-3">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-600 [&_a]:text-blue-600">
            {breadcrumbItems.map((item, i) => (
              <li key={i} className="flex items-center gap-2">
                {i > 0 && <span>/</span>}
                {typeof item.title === 'string' ? item.title : item.title}
              </li>
            ))}
          </ol>
        </nav>

        <h1 className="text-xl font-semibold">{pageTitle}</h1>

        <form onSubmit={handleSubmit}>
          <AppCard className="mb-4 w-full">
            <div className="mb-4">
              <h4 className="text-base font-semibold text-gray-900">
                {locationName}
                {locationKanaDisplay} {locationPeriodDisplay}
              </h4>
            </div>
            {/* 施術者 (shared, full width) */}
            <div className="grid grid-cols-1 items-start gap-x-4 gap-y-1 sm:grid-cols-[120px_minmax(280px,1fr)]">
              <div className="pt-2 sm:pt-2.5">
                <AppAdminFormLabel label="施術者" required className="mb-0" />
              </div>
              <div className="min-w-0">
                <Controller
                  name="massager_id"
                  control={form.control}
                  render={({ field }) => (
                    <AppSelect
                      {...field}
                      placeholder="選択してください"
                      options={massagerOptions}
                      value={field.value && field.value > 0 ? field.value : undefined}
                      onChange={v => field.onChange(v ?? 0)}
                      status={form.formState.errors.massager_id ? 'error' : undefined}
                      className="w-full"
                    />
                  )}
                />
                {form.formState.errors.massager_id && (
                  <p className="mt-1 text-sm text-red-600">
                    {form.formState.errors.massager_id.message}
                  </p>
                )}
              </div>
            </div>
            {massagerIdNum != null && wideAreaQuery.isLoading && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                <Spin size="small" />
                保険者別番号を読み込み中...
              </div>
            )}
          </AppCard>

          {/* A2/R2: start/end times — 00–23h for AM/PM rows on create and edit */}
          <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ServiceBlockFields
              key={`a2-${massagerIdNum ?? 0}-${enableA2Block}-${wideAreaFieldArraySync}`}
              boxPrefix={BLOCK_A2}
              boxLabel="はり・きゅう"
              form={form}
              wideAreaSyncToken={wideAreaFieldArraySync}
              massagerId={massagerIdNum}
              wideAreaLoading={!!massagerIdNum && wideAreaQuery.isLoading}
              disabled={!enableA2Block}
              requirePeriod={enableA2Block && (!enableR2Block || locationServiceBoth)}
              fullDayHourSelect
            />
            <ServiceBlockFields
              key={`r2-${massagerIdNum ?? 0}-${enableR2Block}-${wideAreaFieldArraySync}`}
              boxPrefix={BLOCK_R2}
              boxLabel="マッサージ"
              form={form}
              wideAreaSyncToken={wideAreaFieldArraySync}
              massagerId={massagerIdNum}
              wideAreaLoading={!!massagerIdNum && wideAreaQuery.isLoading}
              disabled={!enableR2Block}
              requirePeriod={enableR2Block && (!enableA2Block || locationServiceBoth)}
              fullDayHourSelect
            />
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <AppButton
              type="primary"
              htmlType="submit"
              disabled={form.formState.isSubmitting || isUpdatePending}
              loading={form.formState.isSubmitting || isUpdatePending}
              className="min-w-[120px]"
            >
              更新
            </AppButton>
            {editMode && onDelete && (
              <AppButton
                type="default"
                danger
                className="min-w-[120px]"
                disabled={isDeletePending || form.formState.isSubmitting || isUpdatePending}
                onClick={() => {
                  const modalInstance = openStandardDeleteConfirm({
                    okButtonProps: { loading: isDeletePending },
                    onOk: async () => {
                      try {
                        await onDelete();
                      } catch (err) {
                        toast.error('エラーが発生しました。');
                        modalInstance.destroy();
                        if (isNotFoundError(err)) {
                          navigate(`/${siteType}/admin/404`, { replace: true });
                        }
                      }
                    },
                  });
                }}
              >
                削除
              </AppButton>
            )}
            <AppButton
              type="default"
              onClick={handleBack}
              className="min-w-[120px]"
              disabled={createMutation.isPending || isUpdatePending || isDeletePending}
            >
              詳細画面へ戻る
            </AppButton>
          </div>
        </form>
        {auditInfo}
      </AppSpace>
    </div>
  );
}

const SERVICE_CHANGED_STORAGE_KEY = 'clinic_location_manager_service_changed';

export default function CreateClinicLocationManagerPage() {
  const navigate = useNavigate();
  const toast = useAppToast();
  const { site, id } = useParams<{ site: string; id: string }>();
  const { currentSite } = useSiteAdminAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const msg = sessionStorage.getItem(SERVICE_CHANGED_STORAGE_KEY);
    if (msg) {
      sessionStorage.removeItem(SERVICE_CHANGED_STORAGE_KEY);
      toast.error('エラーが発生しました。');
    }
  }, [toast]);

  const siteType = site ?? currentSite ?? 'medix';
  const locationId = parseClinicLocationId(id ?? null);

  const locationQuery = useClinicLocationQuery(siteType, id ?? null, {
    refetchOnMount: 'always',
  });
  const data = locationQuery.data?.data;
  const massagers: ClinicLocationMassagerItem[] = data?.massagers ?? [];
  const clinicId = data?.clinic_id ?? 0;

  if (locationQuery.isLoading || locationQuery.isError) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        {locationQuery.isError && (
          <div className="text-red-600">
            {locationQuery.error?.message ?? '拠点の取得に失敗しました。'}
          </div>
        )}
        {locationQuery.isLoading && <Spin size="large" />}
      </div>
    );
  }

  if (!data || locationId == null) {
    return <div className="p-6 text-gray-600">拠点データを読み込めませんでした。</div>;
  }

  if (data.is_parent_clinic_deleted) {
    return <SiteAdminNotFoundTemplate />;
  }

  const locationName = data.location_name ?? '';
  const locationNameKana = data.location_name_kana ?? undefined;
  const availableFrom = data.available_from ?? '';
  const availableTo = data.available_to ?? null;
  const locationService = data.service ?? null;

  return (
    <CreateClinicLocationManagerForm
      locationId={locationId}
      clinicId={clinicId}
      kyotenBreadcrumbSuffix={String(locationId)}
      locationName={locationName}
      locationNameKana={locationNameKana}
      availableFrom={availableFrom}
      availableTo={availableTo}
      massagers={massagers}
      locationService={locationService}
      siteType={siteType}
      navigate={(path, options) => navigate(path, options)}
    />
  );
}
